import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DayPicker } from 'react-day-picker';
import {
  format,
  isValid,
  isAfter,
  addDays,
  startOfMonth,
  endOfMonth,
  addMonths,
} from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';
import numeral from 'numeral';
import useRateCalendar from '../useRateCalendar';
import 'react-day-picker/dist/style.css';

const DATE_FNS_LOCALES = {
  'pt-br': ptBR,
  en: enUS,
  es,
};

const formatDisplay = (date, displayFormat, locale) =>
  isValid(date) ? format(date, displayFormat, { locale }) : '';

const dayKey = date => format(date, 'yyyy-MM-dd');

// Preço compacto no estilo do calendário (ex.: 2070 -> "2,07K", 320 -> "320")
const formatRate = value =>
  value >= 1000
    ? `${(value / 1000).toFixed(2).replace('.', ',')}K`
    : numeral(value).format('0,0');

/**
 * Date range picker baseado em react-day-picker, replicando a UX do antigo
 * DateRangePicker (react-dates): dois inputs (chegada/saída) + popover com o
 * calendário. Suporta modo range (padrão) e single (venda por pacotes).
 *
 * Mostra, em cada dia, a menor diária e o indicador de indisponibilidade
 * (price calendar) — dados vindos de useRateCalendar (mock até o /rateCalendar
 * existir). Dias indisponíveis ficam bloqueados.
 *
 * Trabalha com Date nativo; a conversão para YYYY-MM-DD acontece no pai.
 */
const DateRangeCalendar = ({
  startDate,
  endDate,
  onDatesChange,
  onClear,
  singleMode,
  packageNights,
  numberOfMonths,
  minDate,
  maxDate,
  language,
  startPlaceholder,
  endPlaceholder,
  clearLabel,
  displayFormat,
  adults,
  totalChildren,
  children,
  currency,
}) => {
  const locale = DATE_FNS_LOCALES[language] || enUS;
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(
    isValid(startDate) ? startDate : minDate || new Date()
  );
  const containerRef = useRef(null);

  // Fecha o popover ao clicar fora
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Intervalo visível → busca preços/disponibilidade desses dias
  const visibleFrom = startOfMonth(month);
  const visibleTo = endOfMonth(addMonths(month, numberOfMonths - 1));
  const { days } = useRateCalendar({
    fromDate: visibleFrom,
    toDate: visibleTo,
    adults,
    totalChildren,
    children,
    currency,
    enabled: open,
  });

  // Escolhendo o CHECK-IN: dias indisponíveis ficam bloqueados.
  // Escolhendo o CHECK-OUT (check-in já selecionado): a estadia não pode
  // atravessar uma noite sem disponibilidade. Então o check-out é válido até
  // o PRIMEIRO dia indisponível a partir do check-in (inclusive ele — no dia
  // do check-out o hóspede já está saindo); datas após esse dia são bloqueadas.
  const pickingCheckout =
    !singleMode && isValid(startDate) && !isValid(endDate);

  // primeiro dia indisponível a partir do check-in (último check-out válido)
  let firstUnavailable = null;
  if (pickingCheckout) {
    const limit = isValid(maxDate) ? maxDate : addMonths(startDate, 12);
    let d = startDate;
    while (!isAfter(d, limit)) {
      const info = days[dayKey(d)];
      if (info && info.available === false) {
        firstUnavailable = d;
        break;
      }
      d = addDays(d, 1);
    }
  }

  const disabled = [{ before: minDate }];
  if (isValid(maxDate)) disabled.push({ after: maxDate });
  disabled.push(date => {
    if (pickingCheckout && isAfter(date, startDate)) {
      // fase de check-out: bloqueia datas após o primeiro dia SEM
      // disponibilidade. closed_arrival NÃO interfere aqui (pode ser noite
      // intermediária ou a própria data de saída).
      return firstUnavailable ? isAfter(date, firstUnavailable) : false;
    }
    // fase de check-in (ou datas até o check-in ao reiniciar): bloqueia
    // dias indisponíveis OU fechados para chegada (closedForCheckin)
    const info = days[dayKey(date)];
    if (!info) return false;
    return info.available === false || info.closedForCheckin === true;
  });

  const handleRangeSelect = (range, selectedDay) => {
    // Já existe um período completo: um novo clique recomeça a seleção no dia
    // clicado (limpa o anterior e aguarda novo check-in/check-out), sem fechar.
    if (isValid(startDate) && isValid(endDate) && selectedDay) {
      onDatesChange({ startDate: selectedDay, endDate: null });
      return;
    }
    // Escolhendo o check-out e clicou numa data <= check-in: o react-day-picker
    // montaria um range "pra trás"; em vez disso, reinicia o check-in nessa data
    // (o check-out nunca pode ser anterior ao check-in).
    if (
      isValid(startDate) &&
      !isValid(endDate) &&
      selectedDay &&
      !isAfter(selectedDay, startDate)
    ) {
      onDatesChange({ startDate: selectedDay, endDate: null });
      return;
    }
    onDatesChange({
      startDate: range && range.from ? range.from : null,
      endDate: range && range.to ? range.to : null,
    });
    if (range && range.from && range.to) setOpen(false);
  };

  const handleSingleSelect = day => {
    onDatesChange({ startDate: day || null, endDate: null });
    if (day) setOpen(false);
  };

  // Conteúdo custom de cada dia: número + menor diária / indicador
  const DayContent = useMemo(() => {
    const Comp = ({ date }) => {
      const info = days[dayKey(date)];
      return (
        <span className="rdp-day-cell">
          <span className="rdp-day-num">{format(date, 'd')}</span>
          {info && info.available && info.minRate > 0 && (
            <span className="rdp-day-price">{formatRate(info.minRate)}</span>
          )}
          {info && info.available === false && (
            <span className="rdp-day-x">—</span>
          )}
        </span>
      );
    };
    Comp.propTypes = { date: PropTypes.instanceOf(Date).isRequired };
    return Comp;
  }, [days]);

  const startText = formatDisplay(startDate, displayFormat, locale);
  const endText = formatDisplay(endDate, displayFormat, locale);
  const hasAnyDate = isValid(startDate) || isValid(endDate);
  // No modo pacote, obriga escolher o pacote antes de habilitar a chegada
  const requirePackageFirst = singleMode && !(packageNights > 0);

  const dayPickerCommon = {
    month,
    onMonthChange: setMonth,
    numberOfMonths,
    locale,
    disabled,
    fromDate: minDate,
    toDate: isValid(maxDate) ? maxDate : undefined,
    showOutsideDays: false,
    components: { DayContent },
  };

  return (
    <div className="drp" ref={containerRef}>
      <div className="drp-inputs">
        <button
          type="button"
          className={`drp-input${open ? ' drp-input--active' : ''}`}
          onClick={() => !requirePackageFirst && setOpen(true)}
          disabled={requirePackageFirst}
        >
          {startText || startPlaceholder}
        </button>
        <span className="drp-arrow" aria-hidden="true">→</span>
        <button
          type="button"
          className="drp-input"
          onClick={() => !singleMode && setOpen(true)}
          disabled={singleMode}
        >
          {endText || endPlaceholder}
        </button>
        {hasAnyDate && (
          <button
            type="button"
            className="drp-clear"
            onClick={() => {
              onClear();
              setOpen(false);
            }}
            aria-label={clearLabel}
            title={clearLabel}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <div className="drp-popover">
          {singleMode ? (
            <DayPicker
              mode="single"
              selected={isValid(startDate) ? startDate : undefined}
              onSelect={handleSingleSelect}
              {...dayPickerCommon}
            />
          ) : (
            <DayPicker
              mode="range"
              min={2}
              selected={{
                from: isValid(startDate) ? startDate : undefined,
                to: isValid(endDate) ? endDate : undefined,
              }}
              onSelect={handleRangeSelect}
              {...dayPickerCommon}
            />
          )}
        </div>
      )}
    </div>
  );
};

DateRangeCalendar.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onDatesChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  singleMode: PropTypes.bool,
  packageNights: PropTypes.number,
  numberOfMonths: PropTypes.number,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  language: PropTypes.string,
  startPlaceholder: PropTypes.string,
  endPlaceholder: PropTypes.string,
  clearLabel: PropTypes.string,
  displayFormat: PropTypes.string,
  adults: PropTypes.number,
  totalChildren: PropTypes.number,
  children: PropTypes.array,
  currency: PropTypes.string,
};

DateRangeCalendar.defaultProps = {
  startDate: null,
  endDate: null,
  singleMode: false,
  packageNights: 0,
  numberOfMonths: 2,
  minDate: undefined,
  maxDate: undefined,
  language: 'pt-br',
  startPlaceholder: '',
  endPlaceholder: '',
  clearLabel: '',
  displayFormat: 'dd/MM/yyyy',
  adults: 2,
  totalChildren: 0,
  children: [],
  currency: undefined,
};

export default DateRangeCalendar;
